import React, { useEffect, Fragment, useState, } from 'react';
import { Input, Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { isError, chunk, isEmpty, pickBy, pick, get, keyBy, omit, mapValues, } from 'lodash';
import { useToggle, useList, } from 'react-use';
import { addDays, } from 'date-fns';
import { toast, } from 'react-toastify';
import Select from 'react-select';

import firebase, { functions } from '../../firebase';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useFormState from '../hooks/useFormState';
import Field from '../Field';
import { getAddressByPostalCode, } from '../../util';
import { batch, } from '../../shared/firebase';
import { deliveryTimes, } from '../../shared/config';
import { fields as _fields, adminFields, itemFields, combinedItemFields, getCombinedItems, deliveryType, computeAmazonCombiningRequestAllItems, } from '../../shared/models/combiningRequest';
import { getCombinedItems as getTemplateCombinedItems, } from '../../shared/models/combiningTemplate';
import ListForm from '../ListForm';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import OverlayLoading from '../OverlayLoading';

const db = firebase.firestore();
const fetchFulfillmentInboundShipmentPlan = functions.httpsCallable('fetchFulfillmentInboundShipmentPlan');
const { entries, } = Object;

export default function CombiningRequestFormModal(props) {
  const { isOpen, values, senders, tenant, products, newItems, onClickClose, admin = false, } = props;
  const combiningTemplates = useCollectionSubscription(tenant.ref.collection('combiningTemplates').orderBy('createdAt'), [tenant]);
  const combiningTemplateOptions = combiningTemplates.map(_ => ({ label: _.name, value: _.id }));
  const [combiningTemplateId, setCombiningTemplateId] = useState(null);
  const [number, setNumber] = useState(1);
  const isNew = !values || !values.id;
  const fields = admin ? adminFields() : _fields({ products });
  const productOptions = products.map((product) => {
    const { id, ref, sku, code, name, asin, } = product;
    return {
      value: id,
      queryLabel: [id, sku, code, asin].join(' '),
      label: (
        <ProductSelectOptionLabel product={product} />
      ),
    };
  });
  const productsById = keyBy(products, 'id');
  const productsBySku = keyBy(products, 'sku');
  const statedFields = useFormState(values, fields, isOpen);
  const [items, { set: setItems, updateAt: updateItemAt }] = useList(values.items || newItems || [{ productId: null, quantity: 1, }]);
  const [combinedItems, { set: setCombinedItems, updateAt: updateCombinedItemAt }] = useList(getCombinedItems(values).length > 0 ? getCombinedItems(values) : [{ productId: null, quantity: 1, }]);
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const isUnsubmittable =
    Object.values(statedFields).some(_ => !_.isValid) ||
    (!admin && items.length === 0) ||
    (!admin && items.some(_ => !_.isValid)) ||
    (!admin && combinedItems.length === 0) ||
    (!admin && combinedItems.some(_ => !_.isValid));
  const onSubmit = async (event) => {
    event.preventDefault();
    if(isUnsubmittable) return;

    toggleSubmitting(true);
    try {
      const _items = items.map(_ => ({ ..._, sku: get(productsById, [_.productId, 'sku'], ''), quantity: _.quantity, }));
      const _combinedItems = combinedItems.map(_ => ({ ..._, sku: get(productsById, [_.productId, 'sku'], ''), quantity: _.quantity, }));
      let plans = null;
      await props.onSubmit({ ...mapValues(statedFields, 'value'), items: _items.map(_ => omit(_, 'isValid')), combinedItems: _combinedItems.map(_ => omit(_, 'isValid')), });
    } catch(e) {
      toast.error(`エラーが発生しました (${e.message})`, { autoClose: false });
      throw e;
    } finally {
      toggleSubmitting(false);
    }
  };
  const onClickApply = () => {
    const combiningTemplate = combiningTemplates.find(_ => _.id === combiningTemplateId);
    setItems(combiningTemplate.items.map((item) => {
      const { productId, quantity } = item;
      return {
        productId,
        quantity: quantity * number,
      };
    }));
    setCombinedItems(getTemplateCombinedItems(combiningTemplate).map((item) => {
      const { productId, quantity } = item;
      return {
        productId,
        quantity: quantity * number,
      };
    }));
    setCombiningTemplateId(null);
    setNumber(1);
  };

  return (
    <Modal className="position-relative" isOpen={isOpen} style={{ minWidth: 800 }}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            {isNew ? '追加' : '編集'}
          </h5>
        </div>
      </div>
      <Form onSubmit={onSubmit}>
        <ModalBody className="overflow-auto" style={{ minHeight: 600, maxHeight: '70vh' }}>
          <div className="my-3 d-flex justify-content-end gap-1">
            <div style={{ width: 200 }}>
              <Select
                options={combiningTemplateOptions}
                value={combiningTemplateOptions.find(_ => _.value === combiningTemplateId) || null}
                onChange={_ => setCombiningTemplateId(_?.value)}
                className="w-100"
                placeholder="加工組"
              />
            </div>
            <Input type="number" className="text-right" step="1" value={number} onChange={_ => setNumber(parseInt(_.target.value, 10))} style={{ width: 'auto' }} />
            <Button onClick={onClickApply} disabled={combiningTemplateId == null || number < 1}>
              適用
            </Button>
          </div>
          {
            !admin && (
              <div>
                <h5>加工元商品</h5>
                <ListForm
                  initialItem={_ => ({ quantity: 1, })}
                  addButtonLabel="加工元商品追加"
                  items={items}
                  renderItem={(item, itemIndex) => {
                    return (
                      <div className="card p-3">
                        <ItemForm
                          fields={itemFields({ items, products, })}
                          index={itemIndex}
                          values={item}
                          product={productsById[item.productId]}
                          products={products}
                          productOptions={productOptions}
                          tenant={tenant}
                          onChange={_ => updateItemAt(itemIndex, { ...item, ..._, })}
                          admin={admin}
                        />
                      </div>
                    );
                  }}
                  onChange={setItems}
                  minItems={1}
                />
                <hr className="my-5" />
                <h5>完成商品</h5>
                <ListForm
                  initialItem={_ => ({ quantity: 1, })}
                  addButtonLabel="完成商品追加"
                  items={combinedItems}
                  renderItem={(item, itemIndex) => {
                    return (
                      <div className="card p-3">
                        <ItemForm
                          fields={combinedItemFields({ items: combinedItems, products, })}
                          index={itemIndex}
                          values={item}
                          product={productsById[item.productId]}
                          products={products}
                          productOptions={productOptions}
                          tenant={tenant}
                          onChange={_ => updateCombinedItemAt(itemIndex, { ...item, ..._, })}
                          admin={admin}
                        />
                      </div>
                    );
                  }}
                  onChange={setCombinedItems}
                  minItems={1}
                />
              </div>
            )
          }
          <div className="mt-3">
            {
              entries(statedFields).map(([fieldName, fieldSetting]) => {
                return (
                  <Field
                    key={fieldName}
                    name={fieldName}
                    {...fieldSetting}
                  />
                );
              })
            }
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
          <Button className="save" type="submit" color="primary" onClick={onSubmit} disabled={isUnsubmittable || isSubmitting}>保存</Button>
        </ModalFooter>
      </Form>
      <OverlayLoading isOpen={isSubmitting} label="処理中..." />
    </Modal>
  );
};

function ItemForm (props) {
  const { index: itemIndex, fields, values, tenant, product, onChange, admin = false, disabled = false, products, productOptions, } = props;
  const statedFields = useFormState(values, fields, values);
  useEffect(() => {
    onChange({
      ...mapValues(statedFields, 'value'),
      isValid: Object.values(statedFields).every(_ => _.isValid),
    });
  }, [...Object.values(statedFields).map(_ => _.value), ...Object.values(statedFields).map(_ => _.isValid)]);

  return (
    <div>
      <div className="d-flex">
        {
          entries(statedFields).map(([fieldName, fieldSetting], i) => (
            <div className="mr-2" style={{ minWidth: fieldName === 'productId' && 500 }}>
              <Field
                key={fieldName}
                name={fieldName}
                {...fieldSetting}
                readOnly={_ => disabled}
                {...(itemIndex === 0 && i === 0 && { inputProps: { autoFocus: true, } })}
                {...(fieldName === 'productId' && {
                  options: productOptions,
                  inputProps: {
                    styles: { 
                      valueContainer: _ => ({ ..._, height: 60 }),
                      singleValue: _ => ({ ..._, top: 0, transform: '' }),
                    },
                  },
                })}
              />
            </div>
          ))
        }
      </div>
    </div>
  );
}
